// Color helpers — pure utilities for hex parsing, validation, and palette
// interpolation. Ported from the HTML prototype (lines 691–716).

import type { Hex, RgbTriplet } from '../types';

const HEX_FULL = /^#?[0-9a-fA-F]{6}$/;
const HEX_SHORT = /^#?[0-9a-fA-F]{3}$/;

export function hexToRgb(hex: Hex): RgbTriplet {
  let value = hex.replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const n = Number.parseInt(value, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function isValidHex(s: string): boolean {
  return HEX_FULL.test(s) || HEX_SHORT.test(s);
}

export function normalizeHex(s: string): Hex {
  let value = s.trim();
  if (!value.startsWith('#')) value = `#${value}`;
  return value.toUpperCase();
}

// Linearly interpolate within a discrete palette. Clamps `t` to [0, 1] and
// snaps to integer stops at the boundaries (`i + 1` clamped to `n`).
export function sampleLUT(colors: readonly RgbTriplet[], t: number): RgbTriplet {
  const clamped = Math.max(0, Math.min(1, t));
  const n = colors.length - 1;
  const scaled = clamped * n;
  const i = Math.floor(scaled);
  const f = scaled - i;
  const a = colors[i] ?? colors[0] ?? ([0, 0, 0] as const);
  const b = colors[Math.min(i + 1, n)] ?? a;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}
